import React, { useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { 
  Calendar as CalendarIcon, 
  Grid2X2, 
  Home, 
  Clock, 
  ChevronLeft, 
  ChevronRight, 
  Building2,
  Repeat,
  Zap,
  CalendarDays,
  Menu,
  Users,
  Globe,
  Calculator,
  Database
} from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuGroup,
  DropdownMenuLabel
} from "@/components/ui/dropdown-menu";
import { format, startOfWeek, endOfWeek, addDays, subDays, addWeeks, subWeeks, startOfMonth } from 'date-fns';
import { cn } from '@/lib/utils';
import type { CalendarView, Room } from './BookingCalendar';
import { useDevice } from '@/hooks/use-device';

type ViewSelectorProps = {
  view: CalendarView;
  onViewChange: (view: CalendarView) => void;
  date: Date;
  onDateChange: (date: Date) => void;
  selectedRoom: Room | null;
  onRoomSelect: (room: Room | null) => void;
  rooms: Room[];
  previousView: CalendarView;
};

export const ViewSelector = ({
  view,
  onViewChange,
  date,
  onDateChange,
  selectedRoom,
  onRoomSelect,
  rooms,
  previousView
}: ViewSelectorProps) => {
  const { isMobile, isTablet } = useDevice();
  const isCompactView = isMobile || isTablet;
  const currentMonth = date.getMonth();
  const currentYear = date.getFullYear();

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const weekStart = startOfWeek(date, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(date, { weekStartsOn: 1 });

  const handleHomeClick = useCallback(() => {
    const today = new Date();
    switch (view) {
      case 'weekly':
        onDateChange(startOfWeek(today, { weekStartsOn: 1 }));
        break;
      case 'monthly':
        onDateChange(startOfMonth(today));
        break;
      default:
        onDateChange(today);
    }
    // On mobile, switch to monthly view when clicking home
    onViewChange(isCompactView ? 'monthly' : 'daily');
  }, [onDateChange, onViewChange, view, isCompactView]);

  const handlePrevDate = useCallback(() => {
    if (view === 'weekly') {
      onDateChange(startOfWeek(subWeeks(date, 1), { weekStartsOn: 1 }));
    } else if (view === 'monthly') {
      onDateChange(startOfMonth(subDays(date, 1)));
    } else {
      onDateChange(subDays(date, 1));
    }
  }, [date, onDateChange, view]);

  const handleNextDate = useCallback(() => {
    if (view === 'weekly') {
      onDateChange(startOfWeek(addWeeks(date, 1), { weekStartsOn: 1 }));
    } else if (view === 'monthly') {
      onDateChange(startOfMonth(addDays(date, 32)));
    } else {
      onDateChange(addDays(date, 1));
    }
  }, [date, onDateChange, view]);

  const handleMonthChange = useCallback((monthName: string) => {
    const monthIndex = months.indexOf(monthName); 
    onDateChange(new Date(date.getFullYear(), monthIndex, 1));
  }, [date, onDateChange]);

  const handleViewSwitch = useCallback((newView: CalendarView) => {
    const today = new Date();
    
    if (newView === 'weekly') {
      // When switching to weekly view, always show current week
      onDateChange(startOfWeek(today, { weekStartsOn: 1 }));
    }
    
    onViewChange(newView);
  }, [onDateChange, onViewChange]);

  const handleDateSelect = (newDate: Date | undefined) => {
    if (!newDate) return;
    
    onDateChange(newDate);
    
    // Maintain current view when selecting date
    if (['daily', 'weekly', 'monthly'].includes(view)) {
      // Keep the current view
      return;
    }
    
    // For temporary views, restore previous view
    onViewChange(previousView);
  };

  const iconClass = "h-4 w-4 mr-2";
  const navigationButtonClass = "h-10 w-10 rounded-full bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all p-0 flex items-center justify-center focus:ring-2 focus:ring-offset-2 focus:ring-purple-500";
  const navigationIconClass = "h-4 w-4 text-gray-600";

  // View buttons configuration
  const desktopViewButtons = [
    { view: 'daily' as const, icon: Grid2X2, label: 'Daily View' },
    { view: 'weekly' as const, icon: CalendarIcon, label: 'Weekly View' },
    { view: 'monthly' as const, icon: CalendarDays, label: 'Monthly View' },
    { type: 'spacer' },
    { view: 'quick' as const, icon: Zap, label: 'Quick Booking' },
    { view: 'recurring' as const, icon: Repeat, label: 'Recurring Events' },
    { view: 'current' as const, icon: Clock, label: 'Current Bookings' }
  ];

  // For mobile: hide daily and weekly views, only show monthly as calendar view
  const mobileViewButtons = [
    { view: 'monthly' as const, icon: CalendarDays, label: 'Monthly View' },
    { type: 'spacer' },
    { view: 'quick' as const, icon: Zap, label: 'Quick Booking' },
    { view: 'recurring' as const, icon: Repeat, label: 'Recurring Events' },
    { view: 'current' as const, icon: Clock, label: 'Current Bookings' }
  ];

  // Use appropriate buttons based on device
  const viewButtons = isCompactView ? mobileViewButtons : desktopViewButtons;

  const renderMobileViewSelector = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="icon" className="h-10 w-10">
          <Menu className="h-5 w-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuGroup>
          <DropdownMenuLabel className="font-normal">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <CalendarIcon className="h-4 w-4" />
              View Selection
            </div>
          </DropdownMenuLabel>
        {viewButtons.map((item, index) => 
          item.type === 'spacer' ? (
            <DropdownMenuSeparator key="spacer" />
          ) : (
            <DropdownMenuItem
              key={item.view}
              onClick={() => handleViewSwitch(item.view)}
              className={cn(
                "flex items-center gap-2 py-3",
                view === item.view && "bg-purple-50 text-purple-700"
              )}
            >
              <item.icon className="h-5 w-5" />
              {item.label}
            </DropdownMenuItem>
          )
        )}
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuLabel className="font-normal">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Globe className="h-4 w-4" />
              External Links
            </div>
          </DropdownMenuLabel>
          <DropdownMenuItem asChild>
            <a
              href="https://www.kohdehelsinki.fi"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 py-1.5 cursor-pointer"
            >
              <Globe className="h-5 w-5" />
              Website
            </a>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <a
              href="https://kohdecalculator.netlify.app"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 py-1.5 cursor-pointer"
            >
              <Calculator className="h-5 w-5" />
              Calculator
            </a>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <a
              href="https://docs.google.com/spreadsheets/d/1LR8bUfJF1PF8P2_Hih8oIc3iqtCkeeCfcmj9lLc60Ec/edit?usp=sharing"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 py-1.5 cursor-pointer"
            >
              <Database className="h-5 w-5" />
              Kohde CRM
            </a>
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const renderDesktopViewSelector = () => (
    <div className="flex items-center gap-2">
      {viewButtons.map((item, index) => 
        item.type === 'spacer' ? (
          <div key="spacer" className="w-8" />
        ) : (
          <Button
            key={item.view}
            variant="ghost"
            size="sm"
            onClick={() => handleViewSwitch(item.view)}
            aria-pressed={view === item.view}
            aria-label={item.label}
            className={cn(
              "relative px-4 py-2",
              "transition-all duration-200",
              "border-2 rounded-lg",
              view === item.view ? [
                "bg-purple-50",
                "text-purple-700",
                "border-purple-300",
                "shadow-sm",
                "hover:bg-purple-100",
                "hover:border-purple-400",
                "hover:text-purple-800",
                "active:bg-purple-200"
              ] : [
                "bg-white",
                "border-transparent",
                "text-gray-600",
                "hover:bg-blue-50",
                "hover:text-blue-700",
                "hover:border-blue-200",
                "active:bg-blue-100"
              ]
            )}
          >
            <item.icon className={cn(
              iconClass,
              "transition-colors duration-200",
              view === item.view ? "text-purple-600" : "text-gray-500 group-hover:text-blue-500"
            )} />
            <span className="relative z-10">{item.label}</span>
          </Button>
        )
      )}
    </div>
  );

  const getDateDisplay = () => {
    if (view === 'weekly') {
      return `${format(weekStart, "MMM d")} - ${format(weekEnd, "MMM d, yyyy")}`;
    }
    return `${format(date, "EEEE")}, ${format(date, "PPP")}`;
  };

  // If on mobile and currently in daily or weekly view, switch to monthly
  React.useEffect(() => {
    if (isCompactView && (view === 'daily' || view === 'weekly')) {
      handleViewSwitch('monthly');
    }
  }, [isCompactView, view, handleViewSwitch]);

  return (
    <div className={cn(
      "flex items-center justify-between p-4 border-b",
      isCompactView && "flex-col gap-2"
    )} role="toolbar" aria-label="Calendar navigation">
      <div className={cn(
        "flex items-center gap-2",
        isCompactView && "w-full justify-between items-center"
      )}>
        <div className="flex items-center gap-2">
          {renderMobileViewSelector()}
        </div>
        {!isCompactView && (
          <>
            <div className="w-4" />
            {renderDesktopViewSelector()}
          </>
        )}
        
        {/* Month selector for mobile view */}
        {isCompactView && view === 'monthly' && (
          <Select
            value={format(date, 'MMMM')}
            onValueChange={handleMonthChange}
          >
            <SelectTrigger className="w-[120px] h-10">
              <SelectValue placeholder="Select month" />
            </SelectTrigger>
            <SelectContent>
              {months.map(month => (
                <SelectItem key={month} value={month}>
                  {month} {currentYear}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className={cn(
        "flex items-center gap-4",
        isCompactView && "w-full justify-center"
      )}>
        {view === 'weekly' && !isCompactView && (
          <div className={cn(
            "flex items-center gap-2 bg-purple-50/50 px-3 py-2 rounded-lg border border-purple-100",
            isCompactView && "flex-col w-full my-2"
          )}>
            <Building2 className="h-4 w-4 text-purple-500" />
            <Select
              value={selectedRoom?.id.toString() || "none"}
              onValueChange={(value) => {
                if (value === "none") {
                  onRoomSelect(null);
                } else {
                  const room = rooms.find(r => r.id === parseInt(value));
                  onRoomSelect(room || null);
                }
              }}
            >
              <SelectTrigger className={cn(
                "border-purple-200 bg-white focus:ring-purple-500 min-h-[44px]",
                isCompactView ? "w-full" : "w-[200px]"
              )}>
                <SelectValue placeholder="Select room" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">All Rooms</SelectItem>
                {rooms.map(room => (
                  <SelectItem key={room.id} value={room.id.toString()}>
                    {room.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {view !== 'current' && view !== 'recurring' && view !== 'quick' && (
          <div className={cn(
            "flex items-center gap-2",
            isCompactView && "flex-wrap justify-center w-full"
          )}>
            <Button
              variant="outline"
              size="icon"
              onClick={handlePrevDate}
              className={navigationButtonClass}
              aria-label="Previous"
            >
              <ChevronLeft className={navigationIconClass} />
            </Button>

            {!isCompactView && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="default"
                    className={cn(!date && "text-muted-foreground")}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {getDateDisplay()}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="center">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={handleDateSelect}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            )}

            <Button
              variant="outline"
              size="icon"
              onClick={handleNextDate}
              className={navigationButtonClass}
              aria-label="Next"
            >
              <ChevronRight className={navigationIconClass} />
            </Button>

            {!isCompactView && (
              <Button
                variant="outline"
                size="icon"
                onClick={handleHomeClick}
                className={navigationButtonClass}
              >
                <Home className={navigationIconClass} />
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};